import { api, throwApiError } from "../../../api/api";
import type { DpPaginated } from "./dpTypes";

export type DpDocumentCategory =
  | "CONTRATO"
  | "ADITIVO"
  | "TERMO"
  | "ASO"
  | "NR"
  | "CERTIDAO"
  | "COMPROVANTE"
  | "OUTRO";

export const DOCUMENT_CATEGORY_LABELS: Record<DpDocumentCategory, string> = {
  CONTRATO: "Contrato",
  ADITIVO: "Aditivo",
  TERMO: "Termo",
  ASO: "ASO",
  NR: "Certificado de NR",
  CERTIDAO: "Certidão",
  COMPROVANTE: "Comprovante",
  OUTRO: "Outro",
};

export type DpAsoType =
  | "ADMISSIONAL"
  | "PERIODICO"
  | "DEMISSIONAL"
  | "RETORNO_AO_TRABALHO"
  | "MUDANCA_DE_RISCO";

export const ASO_TYPE_LABELS: Record<DpAsoType, string> = {
  ADMISSIONAL: "Admissional",
  PERIODICO: "Periódico",
  DEMISSIONAL: "Demissional",
  RETORNO_AO_TRABALHO: "Retorno ao trabalho",
  MUDANCA_DE_RISCO: "Mudança de risco",
};

/** Calculada no backend na leitura — "a vencer" muda sozinho com os dias. */
export type DpExpiryStatus = "VALIDO" | "A_VENCER" | "VENCIDO" | "SEM_VALIDADE";

export const EXPIRY_STATUS_LABELS: Record<DpExpiryStatus, string> = {
  VALIDO: "Válido",
  A_VENCER: "A vencer",
  VENCIDO: "Vencido",
  SEM_VALIDADE: "Sem validade",
};

export const EXPIRY_STATUS_BADGE: Record<DpExpiryStatus, string> = {
  VALIDO: "dpBadge--green",
  A_VENCER: "dpBadge--orange",
  VENCIDO: "dpBadge--red",
  SEM_VALIDADE: "dpBadge--blue",
};

export type DpDocumentType = {
  id: string;
  code: string;
  name: string;
  category: DpDocumentCategory;
  requiresExpiry: boolean;
  defaultValidityDays: number | null;
  alertDaysBefore: number;
  isRequiredOnAdmission: boolean;
  isActive: boolean;
};

export type DocumentTypePayload = {
  code: string;
  name: string;
  category: DpDocumentCategory;
  requiresExpiry: boolean;
  defaultValidityDays: number | null;
  alertDaysBefore: number;
  isRequiredOnAdmission: boolean;
  isActive: boolean;
};

export type DpEmployeeDocument = {
  id: string;
  employeeId: string;
  contractId: string | null;
  /** Opcionais desde o envio em lote: preenchidos depois, documento a documento. */
  documentTypeId: string | null;
  documentType: Pick<DpDocumentType, "id" | "code" | "name" | "category" | "requiresExpiry" | "alertDaysBefore"> | null;
  asoType: DpAsoType | null;
  title: string | null;
  assetId: string | null;
  fileName: string | null;
  issuedAt: string | null;
  expiresAt: string | null;
  notes: string | null;
  supersededById: string | null;
  createdBy: { id: string; nomeCompleto: string };
  createdAt: string;
  expiryStatus: DpExpiryStatus;
  daysToExpire: number | null;
};

export type DpExpiringDocument = DpEmployeeDocument & { employee: { id: string; fullName: string } };

/** Nome do tipo, ou "Sem tipo" para documento enviado em lote e ainda não classificado. */
export const documentTypeName = (d: Pick<DpEmployeeDocument, "documentType">) => d.documentType?.name ?? "Sem tipo";

export type EmployeeDocumentPayload = {
  documentTypeId?: string | null;
  contractId?: string | null;
  asoType?: DpAsoType | null;
  title?: string | null;
  assetId?: string | null;
  fileName?: string | null;
  issuedAt?: string | null;
  expiresAt?: string | null;
  notes?: string | null;
};

/** Edição: o arquivo e o contrato não mudam aqui (arquivo novo = "Substituir"). */
export type UpdateEmployeeDocumentPayload = Pick<
  EmployeeDocumentPayload,
  "documentTypeId" | "asoType" | "title" | "issuedAt" | "expiresAt" | "notes"
>;

/** Tipos e tamanho aceitos no arquivo do documento — espelham o backend. */
export const DOCUMENT_FILE_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/webp"];
export const DOCUMENT_FILE_MAX_BYTES = 10 * 1024 * 1024;

const auth = (token: string) => {
  const raw = token?.startsWith("Bearer ") ? token.slice(7) : token;
  return { headers: { Authorization: `Bearer ${raw}` } };
};

// ── Tipos de documento ──────────────────────────────────────────────────────

export const listDocumentTypesApi = async (token: string, includeInactive = false): Promise<DpDocumentType[]> => {
  try {
    const { data } = await api.get<DpDocumentType[]>("/dp/document-types", {
      ...auth(token),
      params: includeInactive ? { includeInactive: "true" } : {},
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao listar os tipos de documento.");
    throw error;
  }
};

export const createDocumentTypeApi = async (token: string, payload: DocumentTypePayload) => {
  try {
    const { data } = await api.post<DpDocumentType>("/dp/document-types", payload, auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao criar o tipo de documento.");
    throw error;
  }
};

export const updateDocumentTypeApi = async (token: string, id: string, payload: Partial<DocumentTypePayload>) => {
  try {
    const { data } = await api.patch<DpDocumentType>(`/dp/document-types/${id}`, payload, auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao salvar o tipo de documento.");
    throw error;
  }
};

export const deleteDocumentTypeApi = async (token: string, id: string): Promise<void> => {
  try {
    await api.delete(`/dp/document-types/${id}`, auth(token));
  } catch (error) {
    throwApiError(error, "Erro ao excluir o tipo de documento.");
  }
};

// ── Documentos do colaborador ───────────────────────────────────────────────

export const listEmployeeDocumentsApi = async (
  token: string,
  employeeId: string,
  includeSuperseded = false
): Promise<DpEmployeeDocument[]> => {
  try {
    const { data } = await api.get<DpEmployeeDocument[]>(`/dp/employees/${employeeId}/documents`, {
      ...auth(token),
      params: includeSuperseded ? { includeSuperseded: "true" } : {},
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao listar os documentos.");
    throw error;
  }
};

/** Envia o arquivo (PDF ou imagem) e devolve o assetId para o lançamento do documento. */
export const uploadDocumentFileApi = async (
  token: string,
  file: File
): Promise<{ assetId: string; fileName: string | null }> => {
  const form = new FormData();
  form.append("file", file);
  try {
    const { data } = await api.post<{ assetId: string; fileName: string | null }>("/dp/documents/files", form, {
      headers: { ...auth(token).headers, "Content-Type": "multipart/form-data" },
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao enviar o arquivo.");
    throw error;
  }
};

export const createEmployeeDocumentApi = async (
  token: string,
  employeeId: string,
  payload: EmployeeDocumentPayload
): Promise<DpEmployeeDocument> => {
  try {
    const { data } = await api.post<DpEmployeeDocument>(`/dp/employees/${employeeId}/documents`, payload, auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao lançar o documento.");
    throw error;
  }
};

export const supersedeEmployeeDocumentApi = async (
  token: string,
  documentId: string,
  payload: EmployeeDocumentPayload
): Promise<DpEmployeeDocument> => {
  try {
    const { data } = await api.post<DpEmployeeDocument>(`/dp/documents/${documentId}/supersede`, payload, auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao substituir o documento.");
    throw error;
  }
};

export const updateEmployeeDocumentApi = async (
  token: string,
  documentId: string,
  payload: UpdateEmployeeDocumentPayload
): Promise<DpEmployeeDocument> => {
  try {
    const { data } = await api.patch<DpEmployeeDocument>(`/dp/documents/${documentId}`, payload, auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao salvar o documento.");
    throw error;
  }
};

export const deleteEmployeeDocumentApi = async (token: string, documentId: string): Promise<void> => {
  try {
    await api.delete(`/dp/documents/${documentId}`, auth(token));
  } catch (error) {
    throwApiError(error, "Erro ao excluir o documento.");
  }
};

/**
 * O arquivo vem como blob pela rota do DP (com o token do staff) — um link
 * direto não mandaria o Authorization. Quem chama cria e revoga o object URL.
 */
export const getDocumentFileBlobApi = async (token: string, documentId: string): Promise<Blob> => {
  try {
    const { data } = await api.get<Blob>(`/dp/documents/${documentId}/file`, {
      ...auth(token),
      responseType: "blob",
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao baixar o arquivo.");
    throw error;
  }
};

export const listExpiringDocumentsApi = async (
  token: string,
  params: { page?: number; limit?: number; days?: number; category?: DpDocumentCategory; costCenterId?: string }
): Promise<DpPaginated<DpExpiringDocument>> => {
  try {
    const { data } = await api.get<DpPaginated<DpExpiringDocument>>("/dp/documents/expiring", {
      ...auth(token),
      params,
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao listar os vencimentos.");
    throw error;
  }
};

export const getDocumentsSummaryApi = async (token: string): Promise<{ expired: number; expiring: number }> => {
  try {
    const { data } = await api.get<{ expired: number; expiring: number }>("/dp/documents/summary", auth(token));
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao consultar os vencimentos.");
    throw error;
  }
};
